import { APP_STORE_URL } from '@/utils/appStore'

/**
 * The 8 audience landing pages, one per App Store custom product page (CPP).
 *
 * Each `ppid` is the real App Store Connect custom product page ID, so the
 * page CTA deep-links to the matching themed product page instead of the
 * default store listing. IDs verified against ASC on July 14, 2026.
 */
export interface Audience {
  slug: string
  /** ASC custom product page ID (drives ?ppid= on the store URL). */
  ppid: string
  /** Short label used in cross-links and the hub grid. */
  label: string
  /** One-line hook for hub cards and cross-links. */
  hook: string
}

export const AUDIENCES: Audience[] = [
  {
    slug: 'baby-and-pregnancy',
    ppid: 'e4987bb5-5a1c-4cf8-b055-13ea6afa2614',
    label: 'Baby & Pregnancy',
    hook: 'Bottles, pouches and formula are not all equal. See which carry the least plastic.',
  },
  {
    slug: 'clean-beauty',
    ppid: 'd7795ac1-16b1-49eb-b0a8-f1e3bfe2e14e',
    label: 'Clean Beauty',
    hook: 'Cosmetics hide polymers in the ingredient list. Paste any label and see them flagged.',
  },
  {
    slug: 'grocery-shopping',
    ppid: '9b830571-5e07-4eff-bd1b-d94f3bf261d3',
    label: 'Grocery Shopping',
    hook: 'Scan your cart before you buy and get a lower-plastic swap for every item.',
  },
  {
    slug: 'kitchen-and-food-storage',
    ppid: 'bf39246d-e27f-43b2-8624-724329d06eba',
    label: 'Kitchen & Food Storage',
    hook: 'Everywhere heat meets plastic: containers, wraps, cookware and what to swap first.',
  },
  {
    slug: 'reduce-exposure',
    ppid: 'c0803d87-393f-4906-878e-40c998846fa5',
    label: 'Reduce Your Exposure',
    hook: 'Weekly plans and small daily steps that show your exposure trending down.',
  },
  {
    slug: 'lab-tested-science',
    ppid: 'f00d53d9-a7a4-4a2f-8772-c0bf0f591651',
    label: 'The Science',
    hook: 'Every score cites the peer-reviewed study behind it. Check the research yourself.',
  },
  {
    slug: 'water-and-filters',
    ppid: 'd4de72b4-b439-4943-a9b4-2166a41a1bb3',
    label: 'Water & Filters',
    hook: 'A liter of bottled water can hold about 240,000 particles. Scan yours and see.',
  },
  {
    slug: 'zero-waste',
    ppid: 'e6b4bcd1-0499-4e1a-9d5c-f10caa564223',
    label: 'Zero Waste & Refill',
    hook: 'Refill stations, zero-waste stores and markets near you, plus proof of what you avoid.',
  },
]

export function audienceStoreUrl(ppid: string): string {
  return `${APP_STORE_URL}?ppid=${ppid}`
}

export function audienceBySlug(slug: string): Audience | undefined {
  return AUDIENCES.find((a) => a.slug === slug)
}

/** The three audiences after this one (cyclic), for the cross-link row. */
export function siblingAudiences(slug: string): Audience[] {
  const i = AUDIENCES.findIndex((a) => a.slug === slug)
  if (i === -1) return AUDIENCES.slice(0, 3)
  return [1, 2, 3].map((step) => AUDIENCES[(i + step) % AUDIENCES.length])
}
